// plan-modal.js — the "How do you pay for AI?" modal for the Spend
// page. Asks, per provider with spend, which plan the user is on
// (logo + edition) OR whether they pay per-token via an API key. The
// resulting monthly cost anchors the Spend hero's value-vs-pay framing
// so the big "API-equivalent value" number reads as a saving, not a
// bill.
//
// Behavior (decided with the user):
//   - Pops once on first Spend visit when no plan is set.
//   - If skipped without setting, a quiet inline nudge (rendered by
//     spend.js) persists until a plan is set — the modal itself does
//     not re-pop automatically.
//   - Reopened anytime via the "you pay $X/mo" chip in the hero.
//   - Guided per-provider picker, plus an "enter a total instead" link.
//
// Selections live in spendState.planSelections (localStorage-backed via
// page-state.js). This module only reads/writes that + toggles the
// modal element; spend.js re-renders the hero on save.
import { spendState, PLAN_CATALOG, PROVIDER_LOGO, savePlanSelections } from "../page-state.js";
import { escapeHTML } from "../util.js";
import { showToast } from "../ui.js";

// Module-level: the providers the modal is currently editing + a
// callback to re-render the page after save. Set in openPlanModal.
let modalProviders = [];
let onSavedCb = null;
let customMode = false;

function el(id) {
  return document.getElementById(id);
}

// providerRowHTML renders one provider's picker row: logo + name, an
// edition <select> (tiers + "Token-based" + "Custom $"), and either an
// accounts input or a custom-$ input depending on the choice.
function providerRowHTML(provider) {
  const cat = PLAN_CATALOG[provider];
  if (!cat) return "";
  const choice = (spendState.planSelections || {})[provider] || {};
  const accounts = Math.max(1, parseInt(choice.accounts, 10) || 1);
  const tierOpts = cat.tiers
    .map(
      (t) =>
        `<option value="${t.id}" ${choice.tier === t.id ? "selected" : ""}>${escapeHTML(
          t.label,
        )} ($${t.usd}/mo)</option>`,
    )
    .join("");
  const tokenOpt = `<option value="tokens" ${choice.tier === "tokens" ? "selected" : ""}>Token-based (API key)</option>`;
  const customOpt = `<option value="custom" ${choice.tier === "custom" ? "selected" : ""}>Custom $/mo…</option>`;
  const noneOpt = `<option value="" ${!choice.tier ? "selected" : ""}>Select…</option>`;

  let trailer = "";
  if (choice.tier === "custom") {
    trailer = `<input type="number" class="pm-custom" data-provider="${provider}" min="0" step="1"
        value="${Number(choice.customUSD) || 0}" placeholder="$/mo" style="width: 96px;" />`;
  } else if (choice.tier === "tokens") {
    trailer = `<span class="pm-token-note">paid per-token — counts as metered cost</span>`;
  } else if (choice.tier) {
    trailer = `<span class="pm-x">×</span>
      <input type="number" class="pm-accounts" data-provider="${provider}" min="1" step="1"
        value="${accounts}" style="width: 56px;" />
      <span class="pm-accts-label">account${accounts === 1 ? "" : "s"}</span>`;
  }

  return `<div class="pm-row">
    <span class="pm-provider">${PROVIDER_LOGO(cat.logo || provider)}<span>${escapeHTML(cat.label)}</span></span>
    <select class="pm-tier" data-provider="${provider}">${noneOpt}${tierOpts}${tokenOpt}${customOpt}</select>
    <span class="pm-trailer">${trailer}</span>
  </div>`;
}

// renderModalBody paints either the guided per-provider rows or the
// single custom-total field, into #spend-plan-modal-body.
function renderModalBody() {
  const body = el("spend-plan-modal-body");
  if (!body) return;
  if (customMode) {
    const cur = Number((spendState.planSelections || {}).__total) || 0;
    body.innerHTML = `<div class="pm-custom-total">
      <label class="pm-label">Total you pay per month for AI subscriptions</label>
      <div class="pm-total-input"><span>$</span><input type="number" id="pm-total" min="0" step="1" value="${cur || ""}" placeholder="0" /><span>/mo</span></div>
      <div class="pm-ref">Reference: Claude Max $200 · ChatGPT Plus $20 · Gemini Ultra $250 (per account)</div>
      <a href="#" class="pm-switch" data-mode="guided">← pick plans instead</a>
    </div>`;
  } else {
    const rows = modalProviders.map(providerRowHTML).join("");
    body.innerHTML = `<div class="pm-rows">${rows || `<div class="pm-empty">No metered providers yet.</div>`}</div>
      <a href="#" class="pm-switch" data-mode="custom">or enter a total $/mo instead →</a>`;
  }
}

// readGuidedSelections pulls the current control values into
// spendState.planSelections (called on save in guided mode).
function readGuidedSelections() {
  const sel = { ...(spendState.planSelections || {}) };
  delete sel.__total; // guided mode supersedes a prior custom total
  const body = el("spend-plan-modal-body");
  if (!body) return;
  body.querySelectorAll(".pm-tier").forEach((tierEl) => {
    const provider = tierEl.getAttribute("data-provider");
    const tier = tierEl.value;
    if (!tier) {
      delete sel[provider];
      return;
    }
    const row = tierEl.closest(".pm-row");
    const acctEl = row && row.querySelector(".pm-accounts");
    const customEl = row && row.querySelector(".pm-custom");
    sel[provider] = {
      tier,
      accounts: acctEl ? Math.max(1, parseInt(acctEl.value, 10) || 1) : 1,
      customUSD: customEl ? Math.max(0, Number(customEl.value) || 0) : 0,
    };
  });
  spendState.planSelections = sel;
}

// openPlanModal shows the modal. It lists ALL known providers (Claude,
// ChatGPT, Gemini) — not just the ones AC has metered — because a user
// may pay for several even when AC only sees Claude Code usage today;
// the modal is where they declare their real total. (The hero's
// savings math still only counts providers with actual metered spend.)
// Metered providers are surfaced first so the relevant one is on top.
export function openPlanModal(meteredProviders, onSaved) {
  const all = Object.keys(PLAN_CATALOG);
  const metered = (meteredProviders || []).filter((p) => PLAN_CATALOG[p]);
  const rest = all.filter((p) => !metered.includes(p));
  modalProviders = [...metered, ...rest];
  onSavedCb = onSaved || null;
  customMode = !!(spendState.planSelections || {}).__total;
  renderModalBody();
  const m = el("spend-plan-modal");
  if (m) m.style.display = "flex";
}

function closeModal() {
  const m = el("spend-plan-modal");
  if (m) m.style.display = "none";
}

// bindPlanModal wires the modal's controls ONCE. Delegated so it
// survives body re-renders. Call from the page bootstrap.
let bound = false;
export function bindPlanModal() {
  if (bound) return;
  const m = el("spend-plan-modal");
  if (!m) return;
  bound = true;

  // Re-render trailer when a tier changes (accounts vs custom vs token).
  m.addEventListener("change", (e) => {
    if (e.target.classList.contains("pm-tier")) {
      // Persist the in-progress tier so renderModalBody reflects it.
      readGuidedSelections();
      renderModalBody();
    }
  });

  // Switch guided <-> custom.
  m.addEventListener("click", (e) => {
    const sw = e.target.closest(".pm-switch");
    if (sw) {
      e.preventDefault();
      customMode = sw.getAttribute("data-mode") === "custom";
      renderModalBody();
    }
  });

  el("spend-plan-modal-close")?.addEventListener("click", () => {
    spendState._planSkipped = true;
    closeModal();
    if (onSavedCb) onSavedCb(); // re-render so the inline nudge shows
  });
  el("spend-plan-modal-skip")?.addEventListener("click", () => {
    spendState._planSkipped = true;
    closeModal();
    if (onSavedCb) onSavedCb();
  });
  // Backdrop click = skip.
  m.addEventListener("click", (e) => {
    if (e.target === m) {
      spendState._planSkipped = true;
      closeModal();
      if (onSavedCb) onSavedCb();
    }
  });

  el("spend-plan-modal-save")?.addEventListener("click", () => {
    if (customMode) {
      const total = Math.max(0, Number(el("pm-total")?.value) || 0);
      const sel = {};
      // A custom total is stored under a synthetic key so the hero can
      // read one flat number regardless of providers.
      sel.__total = total;
      spendState.planSelections = sel;
    } else {
      readGuidedSelections();
    }
    spendState._planSkipped = false;
    savePlanSelections().catch((e) => {
      showToast("error", "Plan kept on this browser only. The account save failed: " + ((e && e.message) || "unknown error"));
    });
    closeModal();
    if (onSavedCb) onSavedCb();
  });
}
